import { createPlayer } from '../player/model.js';
import { fieldMetrics, orientX, screenXForYard } from '../field/metrics.js';
import { snapToPixel } from '../util/math.js';
import { goalLineDefenseTuning } from '../../arcade/goal-line-defense.js';
const ARCADE_FIELD_WIDTH = 53.33;

const ARCADE_INSIDE_ZONE_SPOTS = {
  offense: [
    ['LT', -0.82, -4.6],
    ['LG', -0.82, -2.3],
    ['C', -0.82, 0.0],
    ['RG', -0.82, 2.3],
    ['RT', -0.82, 4.6],
    ['QB', -2.8, 0.0],
    ['RB', -4.8, 1.0],
    ['TE', -1.12, 6.6],
    ['WR1', -1.18, -12.5],
    ['WR2', -1.18, 12.5],
    ['SLOT', -2.68, 6.9],
  ],
  defense: [
    ['DE1', 0.9, -4.5],
    ['DT1', 0.9, -1.1],
    ['DT2', 0.9, 1.1],
    ['DE2', 0.9, 4.5],
    ['LB1', 3.8, -1.6],
    ['LB2', 3.2, 0.2],
    ['LB3', 3.8, 2.4],
    ['CB1', 7.2, -11.0],
    ['S1', 8.8, 0.2],
    ['S2', 7.5, 2.6],
    ['CB2', 7.2, 11.0],
  ],
} as const;

const ARCADE_OUTSIDE_ZONE_SPOTS = {
  offense: [
    ['LT', -0.82, -4.6],
    ['LG', -0.82, -2.3],
    ['C', -0.82, 0.0],
    ['RG', -0.82, 2.3],
    ['RT', -0.82, 4.6],
    ['QB', -2.8, 0.0],
    ['RB', -5.1, 1.5],
    ['TE', -1.12, 7.1],
    ['WR1', -1.18, -12.5],
    ['WR2', -1.18, 12.5],
    ['SLOT', -2.88, 7.4],
  ],
  defense: [
    ['DE1', 0.9, -4.5],
    ['DT1', 0.9, -1.1],
    ['DT2', 0.9, 1.1],
    ['DE2', 0.9, 4.5],
    ['LB1', 3.8, -1.6],
    ['LB2', 3.2, 0.2],
    ['LB3', 3.8, 2.4],
    ['CB1', 7.4, -10.6],
    ['S1', 8.8, 0.2],
    ['S2', 7.8, 3.2],
    ['CB2', 7.4, 10.6],
  ],
} as const;

const ARCADE_ZONE_READ_SPOTS = {
  offense: [
    ['LT', -0.82, -4.6],
    ['LG', -0.82, -2.3],
    ['C', -0.82, 0.0],
    ['RG', -0.82, 2.3],
    ['RT', -0.82, 4.6],
    ['QB', -2.55, 0.0],
    ['RB', -5.3, -1.75],
    ['TE', -1.12, 6.9],
    ['WR1', -1.18, -12.5],
    ['WR2', -1.18, 12.5],
    ['SLOT', -2.68, 7.2],
  ],
  defense: [
    ['DE1', 0.9, -4.5],
    ['DT1', 0.9, -1.1],
    ['DT2', 0.9, 1.1],
    ['DE2', 0.9, 4.5],
    ['LB1', 3.8, -1.6],
    ['LB2', 3.2, 0.2],
    ['LB3', 3.8, 2.4],
    ['CB1', 7.3, -10.8],
    ['S1', 8.8, 0.2],
    ['S2', 7.7, 3.0],
    ['CB2', 7.3, 10.8],
  ],
} as const;

const ARCADE_POWER_O_SPOTS = {
  offense: [
    ['LT', -0.82, -4.6],
    ['LG', -0.82, -2.3],
    ['C', -0.82, 0.0],
    ['RG', -0.82, 2.3],
    ['RT', -0.82, 4.6],
    ['QB', -1.7, 0.0],
    ['RB', -5.8, 1.2],
    ['TE', -1.12, 6.9],
    ['WR1', -1.38, -11.7],
    ['WR2', -1.38, 11.8],
    ['SLOT', -1.68, 6.1],
  ],
  defense: [
    ['DE1', 0.9, -4.5],
    ['DT1', 0.9, -1.1],
    ['DT2', 0.9, 1.1],
    ['DE2', 0.9, 4.5],
    ['LB1', 3.8, -1.6],
    ['LB2', 3.2, 0.2],
    ['LB3', 3.8, 2.4],
    ['CB1', 7.0, -10.8],
    ['S1', 8.8, 0.2],
    ['S2', 7.3, 2.8],
    ['CB2', 7.0, 10.8],
  ],
} as const;

const ARCADE_DUO_SPOTS = {
  offense: [
    ['LT', -0.82, -4.6],
    ['LG', -0.82, -2.3],
    ['C', -0.82, 0.0],
    ['RG', -0.82, 2.3],
    ['RT', -0.82, 4.6],
    ['QB', -1.95, 0.0],
    ['RB', -5.5, 0.9],
    ['TE', -1.12, 6.6],
    ['WR1', -1.24, -12.1],
    ['WR2', -1.24, 12.1],
    ['SLOT', -1.6, 6.8],
  ],
  defense: [
    ['DE1', 0.9, -4.5],
    ['DT1', 0.9, -1.1],
    ['DT2', 0.9, 1.1],
    ['DE2', 0.9, 4.5],
    ['LB1', 3.8, -1.6],
    ['LB2', 3.2, 0.2],
    ['LB3', 3.8, 2.8],
    ['CB1', 7.1, -10.9],
    ['S1', 8.8, 0.2],
    ['S2', 7.4, 3.1],
    ['CB2', 7.1, 10.9],
  ],
} as const;

const ARCADE_QB_SNEAK_SPOTS = {
  offense: [
    ['LT', -0.62, -4.6],
    ['LG', -0.62, -2.3],
    ['C', -0.62, 0.0],
    ['RG', -0.62, 2.3],
    ['RT', -0.62, 4.6],
    ['QB', -1.05, 0.0],
    ['RB', -3.9, 0.9],
    ['TE', -0.92, 5.8],
    ['WR1', -1.18, -10.9],
    ['WR2', -1.18, 10.9],
    ['SLOT', -1.36, 6.3],
  ],
  defense: [
    ['DE1', 1.02, -4.4],
    ['DT1', 1.02, -1.0],
    ['DT2', 1.02, 1.0],
    ['DE2', 1.02, 4.4],
    ['LB1', 3.5, -1.5],
    ['LB2', 2.95, 0.1],
    ['LB3', 3.5, 2.6],
    ['CB1', 7.0, -10.2],
    ['S1', 8.2, 0.2],
    ['S2', 7.1, 2.8],
    ['CB2', 7.0, 10.2],
  ],
} as const;

const ARCADE_COUNTER_TRAY_SPOTS = {
  offense: [
    ['LT', -0.82, -4.6],
    ['LG', -0.82, -2.3],
    ['C', -0.82, 0.0],
    ['RG', -0.82, 2.3],
    ['RT', -0.82, 4.6],
    ['QB', -1.7, 0.0],
    ['RB', -6.0, -1.2],
    ['TE', -1.12, 6.9],
    ['WR1', -1.28, -10.8],
    ['WR2', -1.28, 10.9],
    ['SLOT', -1.78, 5.5],
  ],
  defense: [
    ['DE1', 0.9, -4.5],
    ['DT1', 0.9, -1.1],
    ['DT2', 0.9, 1.1],
    ['DE2', 0.9, 4.5],
    ['LB1', 3.8, -1.6],
    ['LB2', 3.2, 0.2],
    ['LB3', 3.8, 2.4],
    ['CB1', 7.0, -10.8],
    ['S1', 8.8, 0.2],
    ['S2', 7.1, 2.6],
    ['CB2', 7.0, 10.8],
  ],
} as const;

const ARCADE_TOSS_SWEEP_SPOTS = {
  offense: [
    ['LT', -0.1, -4.6],
    ['LG', -0.1, -2.3],
    ['C', -0.1, 0.0],
    ['RG', -0.1, 2.3],
    ['RT', -0.1, 4.6],
    ['QB', -2.0, 0.0],
    ['RB', -5.6, 2.9],
    ['TE', -0.52, 7.5],
    ['WR1', -0.9, -12.2],
    ['WR2', -0.9, 12.2],
    ['SLOT', -2.8, 7.8],
  ],
  defense: [
    ['DE1', 0.9, -4.5],
    ['DT1', 0.9, -1.1],
    ['DT2', 0.9, 1.1],
    ['DE2', 0.9, 4.5],
    ['LB1', 3.8, -1.6],
    ['LB2', 3.2, 0.2],
    ['LB3', 3.8, 2.4],
    ['CB1', 7.6, -10.8],
    ['S1', 8.9, 0.2],
    ['S2', 7.9, 3.4],
    ['CB2', 7.6, 10.8],
  ],
} as const;

const ARCADE_SLANT_FLAT_SPOTS = {
  offense: [
    ['LT', -0.45, -4.6],
    ['LG', -0.45, -2.3],
    ['C', -0.45, 0.0],
    ['RG', -0.45, 2.3],
    ['RT', -0.45, 4.6],
    ['QB', -3.24, 0.0],
    ['RB', -5.2, -1.7],
    ['TE', -1.42, -6.1],
    ['WR1', -1.44, -12.8],
    ['WR2', -1.44, 12.8],
    ['SLOT', -2.78, 7.1],
  ],
  defense: [
    ['DE1', 0.3, -4.6],
    ['DT1', 0.3, -1.25],
    ['DT2', 0.3, 1.25],
    ['DE2', 0.3, 4.6],
    ['LB1', 4.32, -4.2],
    ['LB2', 4.12, 0.1],
    ['LB3', 4.32, 3.2],
    ['CB1', 8.3, -11.8],
    ['S1', 11.2, -0.8],
    ['S2', 11.2, 4.4],
    ['CB2', 8.3, 11.8],
  ],
} as const;

const ARCADE_GOAL_LINE_FADE_SPOTS = {
  offense: [
    ['LT', -0.45, -4.6],
    ['LG', -0.45, -2.3],
    ['C', -0.45, 0.0],
    ['RG', -0.45, 2.3],
    ['RT', -0.45, 4.6],
    ['QB', -1.3, 0.0],
    ['RB', -5.4, 0.15],
    ['TE', -0.64, -4.2],
    ['WR1', -1.05, -19.25],
    ['WR2', -1.05, 19.25],
    ['SLOT', -0.64, 4.2],
  ],
  defense: [
    ['DE1', 0.06, -4.6],
    ['DT1', 0.06, -1.25],
    ['DT2', 0.06, 1.25],
    ['DE2', 0.06, 4.6],
    ['LB1', 1.7, -4.55],
    ['LB2', 1.55, 0.2],
    ['LB3', 1.7, 4.35],
    ['CB1', 1.45, -17.2],
    ['S1', 3.25, -7.4],
    ['S2', 3.4, 7.4],
    ['CB2', 1.45, 17.2],
  ],
} as const;

const ARCADE_KICK_LINE_Y_OFFSETS = [-12.13, -8.09, -4.04, 0, 4.04, 8.09, 12.13] as const;
const ARCADE_KICK_WING_Y_OFFSETS = [-18.22, 18.22] as const;
const ARCADE_OFFENSIVE_LINE_ROLES = new Set(['LT', 'LG', 'C', 'RG', 'RT']);
const ARCADE_DEFENSIVE_FRONT_ROLES = new Set(['DE1', 'DT1', 'DT2', 'DE2']);

function arcadeShellFieldXOffset(role: string, fieldXOffset: number, direction: number): number {
  if (ARCADE_OFFENSIVE_LINE_ROLES.has(role)) {
    return -0.54;
  }
  if (ARCADE_DEFENSIVE_FRONT_ROLES.has(role)) {
    return 0.84;
  }
  return fieldXOffset;
}

function buildArcadeInsideZoneFormation(losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const make = ([role, fieldXOffset, fieldYOffset]: readonly [string, number, number]) => createPlayer(
    role,
    orientX(losX, arcadeShellFieldXOffset(role, fieldXOffset, direction) * metrics.yardWidth, direction),
    snapToPixel(centerY + fieldYOffset * yScale),
  );

  return {
    offense: ARCADE_INSIDE_ZONE_SPOTS.offense.map(make),
    defense: ARCADE_INSIDE_ZONE_SPOTS.defense.map(make),
  };
}

function buildArcadeOutsideZoneFormation(losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const make = ([role, fieldXOffset, fieldYOffset]: readonly [string, number, number]) => createPlayer(
    role,
    orientX(losX, arcadeShellFieldXOffset(role, fieldXOffset, direction) * metrics.yardWidth, direction),
    snapToPixel(centerY + fieldYOffset * yScale),
  );

  return {
    offense: ARCADE_OUTSIDE_ZONE_SPOTS.offense.map(make),
    defense: ARCADE_OUTSIDE_ZONE_SPOTS.defense.map(make),
  };
}

function buildArcadeZoneReadFormation(losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const make = ([role, fieldXOffset, fieldYOffset]: readonly [string, number, number]) => createPlayer(
    role,
    orientX(losX, arcadeShellFieldXOffset(role, fieldXOffset, direction) * metrics.yardWidth, direction),
    snapToPixel(centerY + fieldYOffset * yScale),
  );

  return {
    offense: ARCADE_ZONE_READ_SPOTS.offense.map(make),
    defense: ARCADE_ZONE_READ_SPOTS.defense.map(make),
  };
}

function buildArcadePowerOFormation(losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const make = ([role, fieldXOffset, fieldYOffset]: readonly [string, number, number]) => createPlayer(
    role,
    orientX(losX, arcadeShellFieldXOffset(role, fieldXOffset, direction) * metrics.yardWidth, direction),
    snapToPixel(centerY + fieldYOffset * yScale),
  );

  return {
    offense: ARCADE_POWER_O_SPOTS.offense.map(make),
    defense: ARCADE_POWER_O_SPOTS.defense.map(make),
  };
}

function buildArcadeDuoFormation(losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const make = ([role, fieldXOffset, fieldYOffset]: readonly [string, number, number]) => createPlayer(
    role,
    orientX(losX, arcadeShellFieldXOffset(role, fieldXOffset, direction) * metrics.yardWidth, direction),
    snapToPixel(centerY + fieldYOffset * yScale),
  );

  return {
    offense: ARCADE_DUO_SPOTS.offense.map(make),
    defense: ARCADE_DUO_SPOTS.defense.map(make),
  };
}

function buildArcadeQBSneakFormation(losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const make = ([role, fieldXOffset, fieldYOffset]: readonly [string, number, number]) => createPlayer(
    role,
    orientX(losX, arcadeShellFieldXOffset(role, fieldXOffset, direction) * metrics.yardWidth, direction),
    snapToPixel(centerY + fieldYOffset * yScale),
  );

  return {
    offense: ARCADE_QB_SNEAK_SPOTS.offense.map(make),
    defense: ARCADE_QB_SNEAK_SPOTS.defense.map(make),
  };
}

function buildArcadeTossSweepFormation(losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const make = ([role, fieldXOffset, fieldYOffset]: readonly [string, number, number]) => createPlayer(
    role,
    orientX(losX, arcadeShellFieldXOffset(role, fieldXOffset, direction) * metrics.yardWidth, direction),
    snapToPixel(centerY + fieldYOffset * yScale),
  );

  return {
    offense: ARCADE_TOSS_SWEEP_SPOTS.offense.map(make),
    defense: ARCADE_TOSS_SWEEP_SPOTS.defense.map(make),
  };
}

function buildArcadeCounterTrayFormation(losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const make = ([role, fieldXOffset, fieldYOffset]: readonly [string, number, number]) => createPlayer(
    role,
    orientX(losX, arcadeShellFieldXOffset(role, fieldXOffset, direction) * metrics.yardWidth, direction),
    snapToPixel(centerY + fieldYOffset * yScale),
  );

  return {
    offense: ARCADE_COUNTER_TRAY_SPOTS.offense.map(make),
    defense: ARCADE_COUNTER_TRAY_SPOTS.defense.map(make),
  };
}

function buildArcadeSlantFlatFormation(losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const make = ([role, fieldXOffset, fieldYOffset]: readonly [string, number, number]) => createPlayer(
    role,
    orientX(losX, arcadeShellFieldXOffset(role, fieldXOffset, direction) * metrics.yardWidth, direction),
    snapToPixel(centerY + fieldYOffset * yScale),
  );

  return {
    offense: ARCADE_SLANT_FLAT_SPOTS.offense.map(make),
    defense: ARCADE_SLANT_FLAT_SPOTS.defense.map(make),
  };
}

function buildArcadeGoalLineFadeFormation(losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const make = ([role, fieldXOffset, fieldYOffset]: readonly [string, number, number]) => createPlayer(
    role,
    orientX(losX, arcadeShellFieldXOffset(role, fieldXOffset, direction) * metrics.yardWidth, direction),
    snapToPixel(centerY + fieldYOffset * yScale),
  );

  return {
    offense: ARCADE_GOAL_LINE_FADE_SPOTS.offense.map(make),
    defense: ARCADE_GOAL_LINE_FADE_SPOTS.defense.map(make),
  };
}

function alignRawArcadeGoalLineDefenseToOpeningFrame(
  play: any,
  formation: { offense: any[]; defense: any[] },
  direction: number,
) {
  const startYardLine = Number(play?.startYardLine);
  const yardsToGoal = 100 - startYardLine;
  const openingFrame = play?.arcadeReplay?.frames?.[0];
  if (
    !Number.isFinite(startYardLine)
    || yardsToGoal > goalLineDefenseTuning.startYards
    || !Array.isArray(openingFrame?.players)
  ) {
    return formation;
  }

  const metrics = fieldMetrics();
  const openingDefenseByRole = new Map(
    openingFrame.players
      .filter((player: any) => player?.side === 'defense' && player?.rendererRole)
      .map((player: any) => [String(player.rendererRole), player]),
  );

  return {
    ...formation,
    defense: formation.defense.map((player) => {
      const openingPlayer: any = openingDefenseByRole.get(player.role);
      if (!openingPlayer || !Number.isFinite(openingPlayer.x) || !Number.isFinite(openingPlayer.y)) {
        return player;
      }
      return {
        ...player,
        x: screenXForYard(openingPlayer.x - 10, direction),
        y: snapToPixel(metrics.fieldTop + (openingPlayer.y / ARCADE_FIELD_WIDTH) * metrics.fieldHeight),
      };
    }),
  };
}

export function buildScrimmageFormation(playOrLosX: any, maybeLosX?: number, maybeCenterY?: number, maybeDirection?: number) {
  const play = typeof playOrLosX === 'number' ? null : playOrLosX;
  const losX = typeof playOrLosX === 'number' ? playOrLosX : (maybeLosX as number);
  const centerY = typeof playOrLosX === 'number' ? (maybeLosX as number) : (maybeCenterY as number);
  const direction = typeof playOrLosX === 'number' ? (maybeCenterY as number) : (maybeDirection as number);
  const formationFamily = play?.formationFamily || 'balanced';
  const backfieldSet = play?.backfieldSet || 'pistol';
  const coverageShell = play?.coverageShell || 'single-high';
  const boxCount = Math.max(6, Math.min(8, Number(play?.boxCount || 7)));
  const pressureLook = play?.pressureLook || 'standard';
  const personnelGroup = play?.personnelGroup || '11';

  const lineX = -12;
  const receiverX = formationFamily === 'wide'
    ? -94
    : formationFamily === 'condensed'
      ? -74
      : formationFamily === 'heavy'
        ? -68
        : -84;
  const slotX = formationFamily === 'wide'
    ? -72
    : formationFamily === 'heavy'
      ? -48
      : formationFamily === 'condensed'
        ? -56
        : -64;
  const teX = formationFamily === 'wide'
    ? -44
    : formationFamily === 'heavy'
      ? -34
      : formationFamily === 'condensed'
        ? -42
        : -50;

  const qbX = backfieldSet === 'under-center'
    ? -34
    : backfieldSet === 'gun'
      ? -72
      : -56;
  const rbX = backfieldSet === 'under-center'
    ? -88
    : backfieldSet === 'gun'
      ? -110
      : -96;

  const receiverSpacing = formationFamily === 'wide'
    ? { wr1: -182, wr2: 182, slot: 126, te: -140, rb: 30 }
    : formationFamily === 'condensed'
      ? { wr1: -124, wr2: 124, slot: 74, te: -98, rb: 22 }
      : formationFamily === 'heavy'
        ? { wr1: -116, wr2: 114, slot: personnelGroup === '21' ? 48 : 62, te: -86, rb: 18 }
        : { wr1: -152, wr2: 150, slot: 98, te: -118, rb: 26 };

  const frontX = pressureLook === 'mug' ? 14 : 16;
  const linebackerX = boxCount >= 8 ? 28 : boxCount === 7 ? 42 : 54;
  const cornerX = coverageShell === 'quarters' || coverageShell === 'two-high' ? 84 : 72;
  const deepSafetyX = coverageShell === 'quarters' ? 132 : coverageShell === 'two-high' ? 122 : 116;
  const robberSafetyX = coverageShell === 'robber' ? 76 : 96;
  const singleHighSafetyX = coverageShell === 'single-high' ? 128 : deepSafetyX;
  const linebackerOffsets = boxCount >= 8
    ? [-70, -4, 66]
    : boxCount === 6
      ? [-94, -2, 90]
      : [-86, -2, 82];
  const defensiveLineOffsets = pressureLook === 'edge'
    ? [-70, -18, 18, 70]
    : [-56, -20, 16, 52];

  return {
    offense: [
      createPlayer('LT', orientX(losX, lineX, direction), centerY - 70),
      createPlayer('LG', orientX(losX, lineX, direction), centerY - 36),
      createPlayer('C', orientX(losX, lineX, direction), centerY - 2),
      createPlayer('RG', orientX(losX, lineX, direction), centerY + 32),
      createPlayer('RT', orientX(losX, lineX, direction), centerY + 66),
      createPlayer('QB', orientX(losX, qbX, direction), centerY - 2),
      createPlayer('RB', orientX(losX, rbX, direction), centerY + receiverSpacing.rb),
      createPlayer('TE', orientX(losX, teX, direction), centerY + receiverSpacing.te),
      createPlayer('WR1', orientX(losX, receiverX, direction), centerY + receiverSpacing.wr1),
      createPlayer('WR2', orientX(losX, receiverX, direction), centerY + receiverSpacing.wr2),
      createPlayer('SLOT', orientX(losX, slotX, direction), centerY + receiverSpacing.slot),
    ],
    defense: [
      createPlayer('DE1', orientX(losX, frontX, direction), centerY + defensiveLineOffsets[0]),
      createPlayer('DT1', orientX(losX, frontX, direction), centerY + defensiveLineOffsets[1]),
      createPlayer('DT2', orientX(losX, frontX, direction), centerY + defensiveLineOffsets[2]),
      createPlayer('DE2', orientX(losX, frontX, direction), centerY + defensiveLineOffsets[3]),
      createPlayer('LB1', orientX(losX, pressureLook === 'mug' ? 22 : linebackerX, direction), centerY + linebackerOffsets[0]),
      createPlayer('LB2', orientX(losX, pressureLook === 'mug' ? 24 : linebackerX, direction), centerY + linebackerOffsets[1]),
      createPlayer('LB3', orientX(losX, pressureLook === 'mug' ? 22 : linebackerX, direction), centerY + linebackerOffsets[2]),
      createPlayer('CB1', orientX(losX, cornerX, direction), centerY - (formationFamily === 'wide' ? 172 : 146)),
      createPlayer('CB2', orientX(losX, cornerX, direction), centerY + (formationFamily === 'wide' ? 170 : 144)),
      createPlayer('S1', orientX(losX, coverageShell === 'robber' ? singleHighSafetyX : deepSafetyX, direction), centerY - (coverageShell === 'quarters' ? 82 : coverageShell === 'two-high' ? 66 : 18)),
      createPlayer('S2', orientX(losX, coverageShell === 'robber' ? robberSafetyX : coverageShell === 'single-high' ? 78 : deepSafetyX, direction), centerY + (coverageShell === 'quarters' ? 78 : coverageShell === 'two-high' ? 62 : coverageShell === 'single-high' ? 18 : 6)),
    ],
  };
}

export function buildKickoffFormation(play: any, losX: number, centerY: number, direction: number) {
  const coverageYs = [-172, -134, -96, -58, -20, 20, 58, 96, 134, 172];
  const frontLineYs = [-122, -60, 0, 60, 122];
  const returnWallYs = [-94, -30, 30, 94];
  const landingYard = Math.max(80, Math.min(100, play.landingYardLine ?? 95));
  const coverageLineYard = 60;
  const frontYard = 65;
  const wallYard = 70;
  const deepReturnYard = Math.max(92, Math.min(98, landingYard + 1));

  return {
    offense: [
      createPlayer('KICKER', screenXForYard(35, direction), centerY - 4),
      ...coverageYs.map((offset, index) => createPlayer('COVER' + (index + 1), screenXForYard(coverageLineYard, direction), centerY + offset)),
    ],
    defense: [
      ...frontLineYs.map((offset, index) => createPlayer('FRONT' + (index + 1), screenXForYard(frontYard, direction), centerY + offset)),
      ...returnWallYs.map((offset, index) => createPlayer('WALL' + (index + 1), screenXForYard(wallYard, direction), centerY + offset)),
      createPlayer('RETURNER1', screenXForYard(deepReturnYard, direction), centerY - 50),
      createPlayer('RETURNER2', screenXForYard(Math.max(90, deepReturnYard - 1), direction), centerY + 50),
    ],
  };
}

function buildArcadeKickoffFormation(play: any, losX: number, centerY: number, direction: number) {
  const openingPlayers = play.arcadeReplay?.frames?.[0]?.players;
  if (Array.isArray(openingPlayers) && openingPlayers.length > 0) {
    const metrics = fieldMetrics();
    const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
    const fromReplay = (side: 'offense' | 'defense') => openingPlayers
      .filter((player: any) => player.side === side && player.rendererRole)
      .map((player: any) => createPlayer(
        player.rendererRole,
        screenXForYard(Number(player.x) - 10, direction),
        snapToPixel(centerY + (Number(player.y) - ARCADE_FIELD_WIDTH / 2) * yScale),
      ));
    const offense = fromReplay('offense');
    const defense = fromReplay('defense');
    if (offense.length === 11 && defense.length === 11) {
      return { offense, defense };
    }
  }

  const coverageYs = [-172, -134, -96, -58, -20, 20, 58, 96, 134, 172];
  const frontLineYs = [-122, -60, 0, 60, 122];
  const returnWallYs = [-94, -30, 30, 94];
  const onsideKick = play.arcadeReplay?.playCall === 'onside-kick'
    || String(play.kickoffOutcome || '').startsWith('onside-');
  if (onsideKick) {
    return {
      offense: [
        createPlayer('KICKER', screenXForYard(33, direction), centerY - 4),
        ...coverageYs.map((offset, index) => createPlayer(
          'COVER' + (index + 1),
          screenXForYard(index % 2 === 0 ? 34 : 33, direction),
          centerY + offset,
        )),
      ],
      defense: [
        ...frontLineYs.map((offset, index) => createPlayer(
          'FRONT' + (index + 1),
          screenXForYard(index % 2 === 0 ? 46 : 47, direction),
          centerY + offset,
        )),
        ...returnWallYs.map((offset, index) => createPlayer('WALL' + (index + 1), screenXForYard(51, direction), centerY + offset)),
        createPlayer('RETURNER1', screenXForYard(55, direction), centerY - 50),
        createPlayer('RETURNER2', screenXForYard(55, direction), centerY + 50),
      ],
    };
  }
  const landingYard = Math.max(80, Math.min(100, play.landingYardLine ?? 95));
  // The authoritative kickoff replay releases coverage from field X 49.2,
  // five yards farther downfield than the prior shell. Keeping presentation
  // and simulation aligned prevents a live-frame position jump.
  const coverageLineYard = 39.2;
  const frontYard = 65;
  const wallYard = 70;
  const deepReturnYard = Math.max(92, Math.min(98, landingYard + 1));

  return {
    offense: [
      createPlayer('KICKER', screenXForYard(32, direction), centerY - 4),
      ...coverageYs.map((offset, index) => createPlayer('COVER' + (index + 1), screenXForYard(coverageLineYard, direction), centerY + offset)),
    ],
    defense: [
      ...frontLineYs.map((offset, index) => createPlayer('FRONT' + (index + 1), screenXForYard(frontYard, direction), centerY + offset)),
      ...returnWallYs.map((offset, index) => createPlayer('WALL' + (index + 1), screenXForYard(wallYard, direction), centerY + offset)),
      createPlayer('RETURNER1', screenXForYard(deepReturnYard, direction), centerY - 50),
      createPlayer('RETURNER2', screenXForYard(Math.max(90, deepReturnYard - 1), direction), centerY + 50),
    ],
  };
}

export function buildPuntFormation(play: any, losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const makeX = (fieldOffset: number) => orientX(losX, fieldOffset * metrics.yardWidth, direction);
  const makeY = (fieldOffset: number) => snapToPixel(centerY + fieldOffset * yScale);
  const lineYs = [-12.13, -8.09, -4.04, 0, 4.04, 8.09, 12.13];
  const landingYard = Math.max(54, Math.min(94, play.landingYardLine ?? play.endYardLine ?? 78));

  return {
    offense: [
      ...lineYs.map((offset, index) => createPlayer('PROTECT' + (index + 1), makeX(-0.82), makeY(offset))),
      createPlayer('UPBACK', makeX(-2.45), makeY(2.53)),
      createPlayer('PUNTER', makeX(-4.6), makeY(-0.2)),
      createPlayer('GUNNER1', makeX(-0.82), makeY(-21.47)),
      createPlayer('GUNNER2', makeX(-0.82), makeY(20.96)),
    ],
    defense: [
      ...lineYs.map((offset, index) => createPlayer('RUSH' + (index + 1), makeX(0.9), makeY(offset))),
      createPlayer('CONTAIN1', makeX(0.92), makeY(-14.93)),
      createPlayer('CONTAIN2', makeX(0.92), makeY(14.42)),
      createPlayer('SHIELD', makeX(2.7), makeY(-0.4)),
      createPlayer('RETURNER', screenXForYard(landingYard, direction), centerY - 2),
    ],
  };
}

function buildArcadePuntFormation(play: any, losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const makeX = (fieldOffset: number) => orientX(losX, fieldOffset * metrics.yardWidth, direction);
  const makeY = (fieldOffset: number) => snapToPixel(centerY + fieldOffset * yScale);
  const lineYs = [-12.13, -8.09, -4.04, 0, 4.04, 8.09, 12.13];
  const replayFieldedYard = play.arcadeReplay?.metadata?.puntFieldedYardLine;
  const landingYard = Math.max(
    -10,
    Math.min(110, replayFieldedYard ?? play.fieldedYardLine ?? play.landingYardLine ?? play.endYardLine ?? 78),
  );

  return {
    offense: [
      ...lineYs.map((offset, index) => createPlayer('PROTECT' + (index + 1), makeX(-0.82), makeY(offset))),
      createPlayer('UPBACK', makeX(-2.45), makeY(2.53)),
      createPlayer('PUNTER', makeX(-4.6), makeY(-0.2)),
      createPlayer('GUNNER1', makeX(-0.82), makeY(-21.47)),
      createPlayer('GUNNER2', makeX(-0.82), makeY(20.96)),
    ],
    defense: [
      ...lineYs.map((offset, index) => createPlayer('RUSH' + (index + 1), makeX(0.9), makeY(offset))),
      createPlayer('CONTAIN1', makeX(0.92), makeY(-14.93)),
      createPlayer('CONTAIN2', makeX(0.92), makeY(14.42)),
      createPlayer('SHIELD', makeX(2.7), makeY(-0.4)),
      createPlayer('RETURNER', screenXForYard(landingYard, direction), centerY - 2),
    ],
  };
}

function buildArcadeFieldGoalFormation(play: any, losX: number, centerY: number, direction: number) {
  const metrics = fieldMetrics();
  const yScale = metrics.fieldHeight / ARCADE_FIELD_WIDTH;
  const makeX = (fieldXOffset: number) => orientX(losX, fieldXOffset * metrics.yardWidth, direction);
  const makeY = (fieldYOffset: number) => snapToPixel(centerY + fieldYOffset * yScale);
  const returnerYard = play.playId === 'pat' ? 99 : 95;

  return {
    offense: [
      ...ARCADE_KICK_LINE_Y_OFFSETS.map((offset, index) => createPlayer('BLOCK' + (index + 1), makeX(-0.82), makeY(offset))),
      createPlayer('HOLDER', makeX(-7.25), makeY(-0.12)),
      createPlayer('KICKER', makeX(-10.35), makeY(2.2)),
      createPlayer('WING1', makeX(-1.95), makeY(ARCADE_KICK_WING_Y_OFFSETS[0])),
      createPlayer('WING2', makeX(-1.95), makeY(ARCADE_KICK_WING_Y_OFFSETS[1])),
    ],
    defense: [
      ...ARCADE_KICK_LINE_Y_OFFSETS.map((offset, index) => createPlayer('RUSH' + (index + 1), makeX(0.58), makeY(offset))),
      createPlayer('EDGE1', makeX(2.05), makeY(ARCADE_KICK_WING_Y_OFFSETS[0])),
      createPlayer('EDGE2', makeX(2.05), makeY(ARCADE_KICK_WING_Y_OFFSETS[1])),
      createPlayer('LEAPER1', makeX(3.05), makeY(0)),
      createPlayer('RETURNER', screenXForYard(returnerYard, direction), makeY(0)),
    ],
  };
}

export function buildFieldGoalFormation(losX: number, centerY: number, direction: number) {
  const lineYs = [-96, -64, -32, 0, 32, 64, 96];

  return {
    offense: [
      ...lineYs.map((offset, index) => createPlayer('BLOCK' + (index + 1), orientX(losX, -20, direction), centerY + offset)),
      createPlayer('HOLDER', orientX(losX, -74, direction), centerY - 2),
      createPlayer('KICKER', orientX(losX, -102, direction), centerY + 18),
      createPlayer('WING1', orientX(losX, -40, direction), centerY - 126),
      createPlayer('WING2', orientX(losX, -40, direction), centerY + 122),
    ],
    defense: [
      ...lineYs.map((offset, index) => createPlayer('RUSH' + (index + 1), orientX(losX, 16, direction), centerY + offset)),
      createPlayer('EDGE1', orientX(losX, 56, direction), centerY - 124),
      createPlayer('EDGE2', orientX(losX, 56, direction), centerY + 120),
      createPlayer('LEAPER1', orientX(losX, 88, direction), centerY - 40),
      createPlayer('LEAPER2', orientX(losX, 88, direction), centerY + 36),
    ],
  };
}

export function buildFormation(play: any, losX: number, centerY: number, direction: number) {
  if (play.arcadeReplay?.source === 'arcade-sim-v1' && play.arcadePlaybackMode === 'raw' && play.playId === 'kickoff') {
    return buildArcadeKickoffFormation(play, losX, centerY, direction);
  }
  if (play.arcadeReplay?.source === 'arcade-sim-v1' && play.arcadePlaybackMode === 'raw' && play.playId === 'punt') {
    return buildArcadePuntFormation(play, losX, centerY, direction);
  }
  if (play.arcadeReplay?.source === 'arcade-sim-v1' && play.arcadePlaybackMode === 'raw' && (play.playId === 'field-goal' || play.playId === 'pat')) {
    return buildArcadeFieldGoalFormation(play, losX, centerY, direction);
  }
  if (play.playId === 'kickoff') return buildKickoffFormation(play, losX, centerY, direction);
  if (play.playId === 'punt') return buildPuntFormation(play, losX, centerY, direction);
  if (play.playId === 'field-goal' || play.playId === 'pat') return buildFieldGoalFormation(losX, centerY, direction);
  if (play.arcadeReplay?.source === 'arcade-sim-v1' && play.arcadePlaybackMode === 'raw') {
    const finishFormation = (formation: { offense: any[]; defense: any[] }) => (
      alignRawArcadeGoalLineDefenseToOpeningFrame(play, formation, direction)
    );
    if (play.playId === 'inside-zone') {
      return finishFormation(buildArcadeInsideZoneFormation(losX, centerY, direction));
    }
    if (play.playId === 'split-zone') {
      return finishFormation(play.arcadeReplay?.metadata?.splitZoneShell === 'duo'
        ? buildArcadeDuoFormation(losX, centerY, direction)
        : buildArcadeInsideZoneFormation(losX, centerY, direction));
    }
    if (play.playId === 'outside-zone') {
      return finishFormation(buildArcadeOutsideZoneFormation(losX, centerY, direction));
    }
    if (play.playId === 'zone-read') {
      return finishFormation(buildArcadeZoneReadFormation(losX, centerY, direction));
    }
    if (play.playId === 'power-o') {
      return finishFormation(buildArcadePowerOFormation(losX, centerY, direction));
    }
    if (play.playId === 'duo') {
      return finishFormation(buildArcadeDuoFormation(losX, centerY, direction));
    }
    if (play.playId === 'trap-wham') {
      return finishFormation(buildArcadeDuoFormation(losX, centerY, direction));
    }
    if (play.playId === 'rb-draw' || play.playId === 'qb-draw') {
      return finishFormation(buildArcadeSlantFlatFormation(losX, centerY, direction));
    }
    if (play.playId === 'qb-sneak') {
      return finishFormation(buildArcadeQBSneakFormation(losX, centerY, direction));
    }
    if (play.playId === 'kneel') {
      return finishFormation(buildArcadeQBSneakFormation(losX, centerY, direction));
    }
    if (play.playId === 'counter-tray') {
      return finishFormation(buildArcadeCounterTrayFormation(losX, centerY, direction));
    }
    if (play.playId === 'toss-sweep') {
      return finishFormation(buildArcadeTossSweepFormation(losX, centerY, direction));
    }
    if (play.playId === 'crack-toss') {
      return finishFormation(play.arcadeReplay?.metadata?.crackTossShell === 'zone-read'
        ? buildArcadeZoneReadFormation(losX, centerY, direction)
        : buildArcadeTossSweepFormation(losX, centerY, direction));
    }
    if (play.playId === 'speed-option' || play.playId === 'boot-keeper') {
      return finishFormation(buildArcadeZoneReadFormation(losX, centerY, direction));
    }
    if (play.playId === 'goal-line-fade') {
      return finishFormation(buildArcadeGoalLineFadeFormation(losX, centerY, direction));
    }
    if (
      play.playId === 'slant-flat'
      || play.playId === 'quick-slant'
      || play.playId === 'curl-flat'
      || play.playId === 'stick'
      || play.playId === 'spacing'
      || play.playId === 'smash'
      || play.playId === 'mesh'
      || play.playId === 'mesh-cross'
      || play.playId === 'shallow-drive'
      || play.playId === 'drive'
      || play.playId === 'levels'
      || play.playId === 'dig'
      || play.playId === 'dagger'
      || play.playId === 'flood-shot'
      || play.playId === 'play-action-post'
      || play.playId === 'boot-flood'
      || play.playId === 'four-verticals'
      || play.playId === 'red-zone-spacing'
      || play.playId === 'screen-left'
      || play.playId === 'wr-bubble-screen'
      || play.playId === 'te-delay-screen'
      || play.playId === 'hail-mary'
    ) {
      return finishFormation(buildArcadeSlantFlatFormation(losX, centerY, direction));
    }
  }
  const fallbackFormation = buildScrimmageFormation(play, losX, centerY, direction);
  return play.arcadeReplay?.source === 'arcade-sim-v1' && play.arcadePlaybackMode === 'raw'
    ? alignRawArcadeGoalLineDefenseToOpeningFrame(play, fallbackFormation, direction)
    : fallbackFormation;
}
